import { Box } from '@mui/material'
import { useParams } from 'react-router-dom'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import InfoIcon from '@mui/icons-material/Info'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import DescriptionIcon from '@mui/icons-material/Description'
import FaceIcon from '@mui/icons-material/Face'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PublicIcon from '@mui/icons-material/Public'
import AssignmentIcon from '@mui/icons-material/Assignment'
import RuleIcon from '@mui/icons-material/Rule'
import { useGetKycV2SessionQuery } from '../../../features/kycv2/kycv2Slice'
import { SessionInfo } from './components/SessionInfo'
import { VerificationResults } from './components/VerificationResults'
import { ExtractedInformation } from './components/ExtractedInformation'
import { DocumentImages } from './components/DocumentImages'
import { FaceMatchDetails } from './components/FaceMatchDetails'
import { LivenessDetails } from './components/LivenessDetails'
import { IpAnalysisDetails } from './components/IpAnalysisDetails'
import { RenaperValidation } from './components/RenaperValidation'
import { RulesExecution } from './components/RulesExecution'
import Spinner from '../../../components/common/spinner/spinner'
import styles from './styles.module.scss'

export const KycV2Session = () => {
  const { sessionId } = useParams()
  const { t } = useTranslation(['common'])

  const { data: sessionData, isLoading } = useGetKycV2SessionQuery({ sessionId })
  const session = sessionData?.data

  // Refs for navigation
  const overviewRef = useRef(null)
  const verificationRef = useRef(null)
  const extractedRef = useRef(null)
  const documentsRef = useRef(null)
  const faceMatchRef = useRef(null)
  const livenessRef = useRef(null)
  const ipAnalysisRef = useRef(null)
  const renaperRef = useRef(null)
  const rulesRef = useRef(null)

  const scrollToSection = (ref) => {
    ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  // Get status for each section
  const getStatusIcon = (status) => {
    if (status === 'Approved') {
      return <CheckCircleIcon fontSize="small" className={styles.approved} />
    } else if (status === 'Declined') {
      return <CancelIcon fontSize="small" className={styles.declined} />
    }
    return null
  }

  const details = session?.details

  if (isLoading) {
    return <Spinner />
  }

  return (
    <Box className={`${styles.container} filter`}>
      {/* Sticky Navigation Bar */}
      {/* {details && (
        <Box className={styles.navigationBar}>
          <Box className={styles.navButtons}>
            <button className={styles.navButton} onClick={() => scrollToSection(overviewRef)}>
              <Box className={styles.navIcon}>
                <InfoIcon fontSize="small" />
              </Box>
              {t('common:overview')}
            </button>

            <button className={styles.navButton} onClick={() => scrollToSection(verificationRef)}>
              <Box className={`${styles.navIcon} ${details.status === 'Approved' ? styles.approved : details.status === 'Declined' ? styles.declined : styles.pending}`}>
                {getStatusIcon(details.status) || <VerifiedUserIcon fontSize="small" />}
              </Box>
              {t('common:verificationResults')}
            </button>

            {details.id_verification && (
              <button className={styles.navButton} onClick={() => scrollToSection(extractedRef)}>
                <Box className={`${styles.navIcon} ${details.id_verification.status === 'Approved' ? styles.approved : details.id_verification.status === 'Declined' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(details.id_verification.status) || <DescriptionIcon fontSize="small" />}
                </Box>
                {t('common:idVerification')}
              </button>
            )}

            {details.id_verification && (
              <button className={styles.navButton} onClick={() => scrollToSection(documentsRef)}>
                <Box className={styles.navIcon}>
                  <DescriptionIcon fontSize="small" />
                </Box>
                {t('common:documents')}
              </button>
            )}

            {details.face_match && (
              <button className={styles.navButton} onClick={() => scrollToSection(faceMatchRef)}>
                <Box className={`${styles.navIcon} ${details.face_match.status === 'Approved' ? styles.approved : details.face_match.status === 'Declined' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(details.face_match.status) || <FaceIcon fontSize="small" />}
                </Box>
                {t('common:faceMatch')}
              </button>
            )}

            {details.liveness && (
              <button className={styles.navButton} onClick={() => scrollToSection(livenessRef)}>
                <Box className={`${styles.navIcon} ${details.liveness.status === 'Approved' ? styles.approved : details.liveness.status === 'Declined' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(details.liveness.status) || <VisibilityIcon fontSize="small" />}
                </Box>
                {t('common:liveness')}
              </button>
            )}

            {details.ip_analysis && (
              <button className={styles.navButton} onClick={() => scrollToSection(ipAnalysisRef)}>
                <Box className={`${styles.navIcon} ${details.ip_analysis.status === 'Approved' ? styles.approved : details.ip_analysis.status === 'Declined' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(details.ip_analysis.status) || <PublicIcon fontSize="small" />}
                </Box>
                {t('common:ipAnalysis')}
              </button>
            )}

            {session?.metadata?.renaperValidation && session?.details?.id_verification?.issuing_state === 'ARG' && (
              <button className={styles.navButton} onClick={() => scrollToSection(renaperRef)}>
                <Box className={`${styles.navIcon} ${session.metadata.renaperValidation.status === 'APPROVED' ? styles.approved : session.metadata.renaperValidation.status === 'REJECTED' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(session.metadata.renaperValidation.status === 'APPROVED' ? 'Approved' : session.metadata.renaperValidation.status === 'REJECTED' ? 'Declined' : null) || <AssignmentIcon fontSize="small" />}
                </Box>
                {t('common:renaperValidation')}
              </button>
            )}

            {session?.metadata?.rulesExecution && (
              <button className={styles.navButton} onClick={() => scrollToSection(rulesRef)}>
                <Box className={`${styles.navIcon} ${session.metadata.rulesExecution.status === 'APPROVED' ? styles.approved : session.metadata.rulesExecution.status === 'REJECTED' ? styles.declined : styles.pending}`}>
                  {getStatusIcon(session.metadata.rulesExecution.status === 'APPROVED' ? 'Approved' : session.metadata.rulesExecution.status === 'REJECTED' ? 'Declined' : null) || <RuleIcon fontSize="small" />}
                </Box>
                {t('common:rulesExecution')}
              </button>
            )}
          </Box>
        </Box>
      )} */}

      <Box ref={overviewRef} className={styles.content}>
        <SessionInfo session={session} />
      </Box>

      <Box ref={verificationRef} className={styles.content}>
        <VerificationResults vendorData={session?.details} metadata={session?.metadata} />
      </Box>

      <Box ref={extractedRef} className={styles.content}>
        <ExtractedInformation vendorData={session?.details} />
      </Box>

      <Box ref={documentsRef} className={styles.content}>
        <DocumentImages vendorData={session?.details} />
      </Box>

      <Box ref={faceMatchRef} className={styles.content}>
        <FaceMatchDetails vendorData={session?.details} />
      </Box>

      <Box ref={livenessRef} className={styles.content}>
        <LivenessDetails vendorData={session?.details} />
      </Box>

      <Box ref={ipAnalysisRef} className={styles.content}>
        <IpAnalysisDetails vendorData={session?.details} />
      </Box>

      {session?.metadata?.renaperValidation && session?.details?.id_verification?.issuing_state === 'ARG' && (
        <Box ref={renaperRef} className={styles.content}>
          <RenaperValidation metadata={session.metadata} />
        </Box>
      )}

      {session?.metadata?.rulesExecution && (
        <Box ref={rulesRef} className={styles.content}>
          <RulesExecution metadata={session.metadata} />
        </Box>
      )}
    </Box>
  )
}
